import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { LogOut, Plus, FileText, Ticket, ArrowRight, ShoppingBag, Scissors, ClipboardCheck, ExternalLink, Loader2, Download } from "lucide-react";
import { useCustomer } from "@/lib/customerAuth";
import { api } from "@/lib/config";
import { Reveal } from "@/lib/motion";
import { toast } from "sonner";

const STATUS_COLOR = {
  new: "border-white/40 text-white/90",
  assigned: "border-[#0055ff] text-[#0055ff]",
  in_progress: "border-[#ff4b00] text-[#ff4b00]",
  resolved: "border-[#22c55e] text-[#22c55e]",
  closed: "border-white/20 text-white/40",
  pending_payment: "border-yellow-500/60 text-yellow-500",
  paid: "border-[#22c55e] text-[#22c55e]",
  in_production: "border-[#ff4b00] text-[#ff4b00]",
  shipped: "border-[#0055ff] text-[#0055ff]",
  completed: "border-[#22c55e] text-[#22c55e]",
  pending_review: "border-white/40 text-white/70",
  approved: "border-[#22c55e] text-[#22c55e]",
  rejected: "border-red-500/60 text-red-500",
  cancelled: "border-white/20 text-white/40",
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
const fmtEuro = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CustomerArea() {
  const { user, logout, authHeaders, loading } = useCustomer();
  const nav = useNavigate();
  const [tab, setTab] = useState("panoramica");
  const [tickets, setTickets] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!user) return;
    const h = { headers: authHeaders() };
    Promise.allSettled([
      api.get("/customers/me/tickets", h),
      api.get("/customers/me/contracts", h),
      api.get("/customers/me/orders", h),
      api.get("/customers/me/quotes", h),
      api.get("/customers/me/reports", h),
    ]).then(([t, c, o, q, r]) => {
      setTickets(t.status === "fulfilled" ? t.value.data : []);
      setContracts(c.status === "fulfilled" ? c.value.data : []);
      setOrders(o.status === "fulfilled" ? o.value.data : []);
      setQuotes(q.status === "fulfilled" ? q.value.data : []);
      setReports(r.status === "fulfilled" ? r.value.data : []);
    }).finally(() => setBusy(false));
  }, [user]);

  if (loading) return <div className="pt-40 text-center font-mono-tech text-white/60">Caricamento...</div>;
  if (!user) return <Navigate to="/area/login" replace />;

  const openTicketsCount = tickets.filter((t) => !["resolved", "closed"].includes(t.status)).length;
  const activeContractsCount = contracts.filter((c) => c.status === "active").length;
  const pendingOrdersCount = orders.filter((o) => ["pending_payment", "paid", "in_production", "shipped"].includes(o.status)).length;

  const TABS = [
    { k: "panoramica", label: "Panoramica", icon: ClipboardCheck },
    { k: "ordini", label: `Ordini Shop (${orders.length})`, icon: ShoppingBag },
    { k: "ticket", label: `Ticket (${tickets.length})`, icon: Ticket },
    { k: "preventivi", label: `Preventivi Laser (${quotes.length})`, icon: Scissors },
    { k: "report", label: `Report Interventi (${reports.length})`, icon: FileText },
    { k: "contratti", label: `Contratti (${contracts.length})`, icon: FileText },
  ];

  return (
    <div className="pt-24 pb-24">
      <section className="border-b border-[#141414]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
          <div>
            <Reveal>
              <div className="num-chip mb-4"><span className="dot" /> Area cliente</div>
              <div className="font-display text-4xl md:text-6xl leading-[0.95]">Ciao, {user.name}.</div>
              <div className="font-mono-tech text-white/50 text-[11px] mt-3">
                {user.email}{user.company && <> · {user.company}</>}
              </div>
            </Reveal>
          </div>
          <div className="flex gap-3">
            <Link to="/ticket" data-testid="area-new-ticket" className="btn-primary"><Plus size={14} /> Nuovo ticket</Link>
            <button onClick={() => { logout(); nav("/"); }} data-testid="area-logout" className="btn-ghost"><LogOut size={14} /> Esci</button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-b border-[#141414] sticky top-[64px] z-30 bg-[#000]/95 backdrop-blur">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex gap-1 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.k}
                  onClick={() => setTab(t.k)}
                  data-testid={`area-tab-${t.k}`}
                  className={`font-mono-tech text-[11px] px-4 py-4 border-b-2 -mb-px whitespace-nowrap flex items-center gap-2 transition-colors ${tab === t.k ? "border-[#ff4b00] text-[#ff4b00]" : "border-transparent text-white/50 hover:text-white"}`}
                >
                  <Icon size={12} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {busy ? (
            <div className="font-mono-tech text-white/40 text-center py-16 flex items-center justify-center gap-3">
              <Loader2 size={16} className="animate-spin" /> Caricamento dati...
            </div>
          ) : (
            <>
              {tab === "panoramica" && (
                <OverviewPanel
                  ticketsOpen={openTicketsCount}
                  ticketsTotal={tickets.length}
                  contractsActive={activeContractsCount}
                  contractsTotal={contracts.length}
                  ordersPending={pendingOrdersCount}
                  ordersTotal={orders.length}
                  quotesTotal={quotes.length}
                  reportsTotal={reports.length}
                  setTab={setTab}
                />
              )}
              {tab === "ordini" && <OrdersTable orders={orders} />}
              {tab === "ticket" && <TicketsTable tickets={tickets} />}
              {tab === "preventivi" && <QuotesTable quotes={quotes} />}
              {tab === "report" && <ReportsTable reports={reports} />}
              {tab === "contratti" && <ContractsGrid contracts={contracts} />}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, sub, accent = "text-white", Icon }) {
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="font-mono-tech text-[10px] text-white/40">{label}</span>
        {Icon && <Icon size={14} className={accent} />}
      </div>
      <div className={`font-display text-4xl ${accent}`}>{value}</div>
      {sub && <div className="font-mono-tech text-[10px] text-white/40 mt-2">{sub}</div>}
    </div>
  );
}

function OverviewPanel({ ticketsOpen, ticketsTotal, contractsActive, contractsTotal, ordersPending, ordersTotal, quotesTotal, reportsTotal, setTab }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Ticket aperti" value={ticketsOpen} sub={`di ${ticketsTotal} totali`} accent="text-[#ff4b00]" Icon={Ticket} />
        <StatCard label="Ordini in corso" value={ordersPending} sub={`di ${ordersTotal} totali`} accent="text-[#0055ff]" Icon={ShoppingBag} />
        <StatCard label="Contratti attivi" value={contractsActive} sub={`di ${contractsTotal} totali`} accent="text-[#22c55e]" Icon={FileText} />
        <StatCard label="Preventivi Laser" value={quotesTotal} sub={`di ${reportsTotal} report`} accent="text-white" Icon={Scissors} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={() => setTab("ordini")} className="text-left border border-[#1a1a1a] bg-[#0a0a0a] p-6 hover:border-[#ff4b00]/40 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <ShoppingBag size={18} className="text-[#ff4b00]" />
            <ArrowRight size={14} className="text-white/40 group-hover:text-[#ff4b00] group-hover:translate-x-1 transition-all" />
          </div>
          <div className="font-display text-2xl mb-2">I miei ordini Shop</div>
          <div className="font-mono-tech text-[10px] text-white/40">Storico acquisti, tracking, ricevute</div>
        </button>
        <button onClick={() => setTab("preventivi")} className="text-left border border-[#1a1a1a] bg-[#0a0a0a] p-6 hover:border-[#ff4b00]/40 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <Scissors size={18} className="text-[#ff4b00]" />
            <ArrowRight size={14} className="text-white/40 group-hover:text-[#ff4b00] group-hover:translate-x-1 transition-all" />
          </div>
          <div className="font-display text-2xl mb-2">Preventivi Taglio Laser</div>
          <div className="font-mono-tech text-[10px] text-white/40">Stato preventivi, stime, pagamenti</div>
        </button>
        <button onClick={() => setTab("ticket")} className="text-left border border-[#1a1a1a] bg-[#0a0a0a] p-6 hover:border-[#ff4b00]/40 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <Ticket size={18} className="text-[#ff4b00]" />
            <ArrowRight size={14} className="text-white/40 group-hover:text-[#ff4b00] group-hover:translate-x-1 transition-all" />
          </div>
          <div className="font-display text-2xl mb-2">Ticket assistenza</div>
          <div className="font-mono-tech text-[10px] text-white/40">Richieste di intervento e stato</div>
        </button>
        <button onClick={() => setTab("report")} className="text-left border border-[#1a1a1a] bg-[#0a0a0a] p-6 hover:border-[#ff4b00]/40 transition-colors group">
          <div className="flex items-center justify-between mb-4">
            <FileText size={18} className="text-[#ff4b00]" />
            <ArrowRight size={14} className="text-white/40 group-hover:text-[#ff4b00] group-hover:translate-x-1 transition-all" />
          </div>
          <div className="font-display text-2xl mb-2">Report interventi</div>
          <div className="font-mono-tech text-[10px] text-white/40">Rapporti tecnici dei tecnici Gefion</div>
        </button>
      </div>
    </div>
  );
}

function EmptyState({ title, subtitle, cta, to }) {
  return (
    <div className="border border-dashed border-[#222] p-12 text-center bg-[#0a0a0a]/50">
      <div className="font-display text-2xl text-white/80 mb-2">{title}</div>
      <div className="font-mono-tech text-[11px] text-white/40 mb-6">{subtitle}</div>
      {cta && to && (
        <Link to={to} className="btn-primary inline-flex">{cta} <ArrowRight size={14} /></Link>
      )}
    </div>
  );
}

function Badge({ status }) {
  return (
    <span className={`px-2 py-1 border font-mono-tech text-[10px] ${STATUS_COLOR[status] || "border-white/20 text-white/40"}`}>
      {status || "—"}
    </span>
  );
}

function OrdersTable({ orders }) {
  if (orders.length === 0) return <EmptyState title="Nessun ordine ancora" subtitle="I tuoi ordini shop appariranno qui" cta="Vai allo shop" to="/shop" />;
  return (
    <div className="border border-[#1a1a1a] overflow-x-auto">
      <table className="w-full text-left font-mono-tech text-[11px] min-w-[720px]">
        <thead className="bg-[#0a0a0a] text-white/50">
          <tr><th className="p-3">Data</th><th className="p-3">Ordine</th><th className="p-3">Articoli</th><th className="p-3">Totale</th><th className="p-3">Pagamento</th><th className="p-3">Stato</th><th className="p-3"></th></tr>
        </thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`order-row-${o.id}`}>
              <td className="p-3 text-white/70">{fmtDate(o.created_at)}</td>
              <td className="p-3 text-white/70">#{(o.id || "").substring(0, 8)}</td>
              <td className="p-3 text-white">{(o.items || []).length} art.</td>
              <td className="p-3 text-white">{fmtEuro(o.total_eur)}</td>
              <td className="p-3 text-white/60">{o.payment?.method || "—"}</td>
              <td className="p-3"><Badge status={o.status} /></td>
              <td className="p-3"><Link to={`/shop/ordine/${o.id}`} className="text-[#ff4b00] hover:underline inline-flex items-center gap-1">Apri <ExternalLink size={10} /></Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TicketsTable({ tickets }) {
  if (tickets.length === 0) return <EmptyState title="Nessun ticket ancora" subtitle="Apri il tuo primo ticket di assistenza" cta="Apri ticket" to="/ticket" />;
  return (
    <div className="border border-[#1a1a1a] overflow-x-auto">
      <table className="w-full text-left font-mono-tech text-[11px] min-w-[720px]">
        <thead className="bg-[#0a0a0a] text-white/50">
          <tr><th className="p-3">Data</th><th className="p-3">Titolo</th><th className="p-3">Divisione</th><th className="p-3">Priorità</th><th className="p-3">Stato</th></tr>
        </thead>
        <tbody>
          {tickets.map((t) => (
            <tr key={t.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`ticket-row-${t.id}`}>
              <td className="p-3 text-white/70">{fmtDate(t.created_at)}</td>
              <td className="p-3 text-white">{t.title}{t.plant_name && <div className="text-white/40 text-[10px] mt-0.5">{t.plant_name}</div>}</td>
              <td className="p-3 text-white/60">{t.area}</td>
              <td className="p-3">
                <span className={`px-2 py-1 border text-[10px] ${t.priority === "urgent" ? "border-red-400 text-red-400" : t.priority === "high" ? "border-[#ff4b00] text-[#ff4b00]" : "border-white/30 text-white/70"}`}>{t.priority}</span>
              </td>
              <td className="p-3"><Badge status={t.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuotesTable({ quotes }) {
  if (quotes.length === 0) return <EmptyState title="Nessun preventivo laser" subtitle="Richiedi un preventivo per il taglio laser" cta="Nuovo preventivo" to="/taglio-laser" />;
  return (
    <div className="border border-[#1a1a1a] overflow-x-auto">
      <table className="w-full text-left font-mono-tech text-[11px] min-w-[820px]">
        <thead className="bg-[#0a0a0a] text-white/50">
          <tr><th className="p-3">Data</th><th className="p-3">Materiale</th><th className="p-3">Qtà</th><th className="p-3">Stima</th><th className="p-3">Stato</th><th className="p-3"></th></tr>
        </thead>
        <tbody>
          {quotes.map((q) => (
            <tr key={q.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`quote-row-${q.id}`}>
              <td className="p-3 text-white/70">{fmtDate(q.created_at)}</td>
              <td className="p-3 text-white">{q.specs?.material_name || "—"}<div className="text-white/40 text-[10px] mt-0.5">{q.specs?.thickness_mm}mm · {q.specs?.width_mm}×{q.specs?.height_mm}</div></td>
              <td className="p-3 text-white/70">{q.specs?.quantity}</td>
              <td className="p-3 text-white">{fmtEuro((q.estimate?.total_with_vat || 0) + (q.shipping?.cost || 0))}</td>
              <td className="p-3"><Badge status={q.status} /></td>
              <td className="p-3"><Link to={`/preventivo/${q.id}`} className="text-[#ff4b00] hover:underline inline-flex items-center gap-1">Apri <ExternalLink size={10} /></Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportsTable({ reports }) {
  if (reports.length === 0) return <EmptyState title="Nessun report ancora" subtitle="I rapporti di intervento tecnico appariranno qui" />;
  return (
    <div className="border border-[#1a1a1a] overflow-x-auto">
      <table className="w-full text-left font-mono-tech text-[11px] min-w-[820px]">
        <thead className="bg-[#0a0a0a] text-white/50">
          <tr><th className="p-3">Data</th><th className="p-3">Impianto</th><th className="p-3">Divisione</th><th className="p-3">Tecnico</th><th className="p-3">Copertura</th><th className="p-3"></th></tr>
        </thead>
        <tbody>
          {reports.map((r) => (
            <tr key={r.id} className="border-t border-[#151515] hover:bg-[#0a0a0a]" data-testid={`report-row-${r.id}`}>
              <td className="p-3 text-white/70">{r.intervention_date}</td>
              <td className="p-3 text-white">{r.plant_name || "—"}</td>
              <td className="p-3 text-white/60">{r.area}</td>
              <td className="p-3 text-white/70">{r.technician_name}</td>
              <td className="p-3">
                {r.is_covered_by_contract ? (
                  <span className="text-[#22c55e] font-mono-tech text-[10px]">In contratto</span>
                ) : (
                  <span className="text-[#ff4b00] font-mono-tech text-[10px]">Fatturabile</span>
                )}
              </td>
              <td className="p-3"><Link to={`/rapporto/${r.id}`} className="text-[#ff4b00] hover:underline inline-flex items-center gap-1">Apri <ExternalLink size={10} /></Link></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContractsGrid({ contracts }) {
  if (contracts.length === 0) return <EmptyState title="Nessun contratto attivo" subtitle="Richiedi un contratto di assistenza personalizzato" cta="Richiedi preventivo" to="/assistenza" />;

  const downloadPdf = async (contractId) => {
    try {
      const r = await api.get(`/customers/me/contracts/${contractId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contratto-${contractId.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (e) { toast.error("Errore nel download PDF"); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {contracts.map((c) => (
        <div key={c.id} className="border border-[#1a1a1a] bg-[#0a0a0a] p-6" data-testid={`contract-card-${c.id}`}>
          <div className="flex items-baseline justify-between">
            <div className="font-display text-2xl">{(c.plan_code || "").toUpperCase()}</div>
            <Badge status={c.status} />
          </div>
          <div className="font-mono-tech text-white/50 text-[11px] mt-2">{c.area_key} · {c.plants_count} impianto{c.plants_count > 1 ? "i" : ""}</div>
          {c.plant_name && <div className="font-mono-tech text-white/60 text-[10px] mt-1">{c.plant_name}</div>}
          <div className="font-mono-tech text-white/70 text-xs mt-4">{c.starts_on} → {c.ends_on}</div>
          <div className="mt-5 pt-5 border-t border-[#1a1a1a] flex items-center justify-between">
            <div className="font-mono-tech text-[10px] text-white/40">N. GEF-{(c.created_at || "").substring(0, 4)}-{c.id.substring(0, 8).toUpperCase()}</div>
            <button
              onClick={() => downloadPdf(c.id)}
              data-testid={`contract-download-${c.id}`}
              className="btn-ghost text-[11px] hover:text-[#ff4b00] hover:border-[#ff4b00]"
            >
              <Download size={12} /> Scarica PDF
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
